import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Users } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-1";

export default function ServicesList() {
  const [services, setServices] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/services`);
      setServices(r.data.services);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-services" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Service catalog</h1>
        <p className="mt-2 text-mid-neutral">
          The services you can assign to clients. Edit descriptions, default pricing and availability.
        </p>
      </header>

      {error && (
        <div data-testid="services-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!services ? (
        <div className="flex items-center justify-center h-40 text-mid-neutral">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading services…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {services.map((s) => (
            <ServiceCard key={s.id} service={s} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceCard({ service, onSaved }) {
  const [draft, setDraft] = useState({
    name: service.name,
    description: service.description || "",
    defaultMonthlyValue: service.defaultMonthlyValue ?? "",
    active: service.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.patch(`${API}/admin/services/${service.id}`, {
        name: draft.name,
        description: draft.description,
        ...(draft.defaultMonthlyValue !== "" ? { defaultMonthlyValue: Number(draft.defaultMonthlyValue) } : {}),
        active: draft.active,
      });
      setMsg("Saved.");
      onSaved();
    } catch (err) {
      setMsg(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`service-card-${service.key}`} className="rounded-2xl bg-white border border-border-neutral p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          data-testid={`service-name-${service.key}`}
          className="flex-1 font-extrabold text-forest bg-soft rounded-lg p-2 text-sm border border-transparent focus:border-mint focus:outline-none"
        />
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-mid-neutral whitespace-nowrap pt-2">
          <Users className="h-3.5 w-3.5" /> {service.activeAssignments} active
        </span>
      </div>
      <textarea
        rows={2}
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        data-testid={`service-description-${service.key}`}
        className={inputCls}
      />
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <label className={labelCls}>Default monthly value (AUD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.defaultMonthlyValue}
            onChange={(e) => setDraft({ ...draft, defaultMonthlyValue: e.target.value })}
            data-testid={`service-value-${service.key}`}
            className={inputCls}
          />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-forest pb-2">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            data-testid={`service-active-${service.key}`}
            className="h-4 w-4 accent-forest"
          />
          Available
        </label>
      </div>
      <div className="flex items-center justify-between">
        {msg && <p data-testid={`service-msg-${service.key}`} className="text-xs font-semibold text-mid-neutral">{msg}</p>}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`service-save-${service.key}`}
          className="ml-auto inline-flex items-center gap-2 rounded-lg bg-forest text-white px-3 py-1.5 text-xs font-bold disabled:opacity-50"
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}
